'use client';

import { TrendingUp, CheckCircle2, AlertCircle, Minus } from 'lucide-react';
import type { MatchScoreBreakdownScore } from '@/lib/types';
import { cn } from '@/lib/utils';
import GlassPanel from '@/components/dashboard/GlassPanel';
import DashboardBadge from '@/components/dashboard/DashboardBadge';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface MatchScoreBreakdownProps {
  score: MatchScoreBreakdownScore;
  /** Treat skills as excluded from scoring when either side has no skill data.
   *  This prevents a misleading 0% from being shown for a neutral dimension. */
  skillsExcluded?: boolean;
  className?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function isSkillsExcluded(
  jobRequiredSkills?: string[] | null,
  candidateSkills?: string[] | null
): boolean {
  if (jobRequiredSkills && jobRequiredSkills.length === 0) return true;
  if (candidateSkills && candidateSkills.length === 0) return true;
  return false;
}

function scoreColorClass(score: number): string {
  if (score >= 80) return 'bg-neon-green';
  if (score >= 50) return 'bg-neon-amber';
  return 'bg-neon-magenta';
}

function scoreTextClass(score: number): string {
  if (score >= 80) return 'text-neon-green';
  if (score >= 50) return 'text-neon-amber';
  return 'text-neon-magenta';
}

function scoreBadgeVariant(score: number): 'green' | 'amber' | 'magenta' {
  if (score >= 80) return 'green';
  if (score >= 50) return 'amber';
  return 'magenta';
}

const DIMENSION_LABELS: Record<keyof Omit<MatchScoreBreakdownScore, 'overallScore' | 'strengths' | 'gaps'>, string> = {
  rankScore: 'Rank / Position',
  experienceScore: 'Experience',
  certificationScore: 'Certifications',
  skillsScore: 'Skills',
  availabilityScore: 'Availability',
  locationScore: 'Location',
  salaryScore: 'Salary',
};

const DIMENSION_KEYS: (keyof typeof DIMENSION_LABELS)[] = [
  'rankScore',
  'experienceScore',
  'certificationScore',
  'skillsScore',
  'availabilityScore',
  'locationScore',
  'salaryScore',
];

// ─── Component ───────────────────────────────────────────────────────────────

export function MatchScoreBreakdown({
  score,
  skillsExcluded = false,
  className,
}: MatchScoreBreakdownProps) {
  const { overallScore, strengths = [], gaps = [] } = score;

  return (
    <GlassPanel className={cn('space-y-5', className)}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className={cn('h-5 w-5', scoreTextClass(overallScore))} />
          <h3 className="text-sm font-semibold text-foreground">Match breakdown</h3>
        </div>
        <DashboardBadge variant={scoreBadgeVariant(overallScore)} className="text-sm">
          {overallScore}% overall
        </DashboardBadge>
      </div>

      {/* Dimension bars */}
      <div className="space-y-3">
        {DIMENSION_KEYS.map((key) => {
          const isSkills = key === 'skillsScore';
          const excluded = isSkills && skillsExcluded;
          const value = excluded ? 0 : score[key];
          const label = DIMENSION_LABELS[key];

          return (
            <div key={key} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className={cn('font-medium', excluded ? 'text-ash' : 'text-foreground')}>
                  {label}
                </span>
                <span
                  className={cn(
                    'tabular-nums font-semibold',
                    excluded ? 'text-ash' : scoreTextClass(value)
                  )}
                  aria-label={excluded ? `${label}: not scored` : `${label}: ${value}%`}
                >
                  {excluded ? 'N/A' : `${value}%`}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn('h-full rounded-full transition-all', excluded ? 'bg-ash' : scoreColorClass(value))}
                  style={{ width: excluded ? '0%' : `${value}%` }}
                  aria-hidden="true"
                />
              </div>
              {excluded && (
                <p className="text-xs text-ash">
                  Not scored — no skill data on one side of this pairing.
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Strengths & Gaps */}
      {(strengths.length > 0 || gaps.length > 0) && (
        <div className="grid gap-4 border-t border-neon-cyan/10 pt-4 sm:grid-cols-2">
          {strengths.length > 0 && (
            <div className="space-y-2">
              <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-neon-green">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Strengths
              </h4>
              <ul className="space-y-1.5">
                {strengths.map((item, i) => (
                  <li key={`strength-${i}`} className="flex items-start gap-2 text-sm text-foreground">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neon-green" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {gaps.length > 0 && (
            <div className="space-y-2">
              <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-neon-amber">
                <AlertCircle className="h-3.5 w-3.5" />
                Gaps
              </h4>
              <ul className="space-y-1.5">
                {gaps.map((item, i) => (
                  <li key={`gap-${i}`} className="flex items-start gap-2 text-sm text-foreground">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neon-amber" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {strengths.length === 0 && gaps.length === 0 && (
        <p className="flex items-center gap-2 border-t border-neon-cyan/10 pt-4 text-sm text-ash">
          <Minus className="h-4 w-4" />
          No specific strengths or gaps identified for this pairing.
        </p>
      )}
    </GlassPanel>
  );
}

export default MatchScoreBreakdown;
